import { FilterGroup, FilterOption } from "@amber/ui";
import type { TasksResponse } from "../../contracts/index";
import type { TasksSearch } from "../search";

function toggledAge(current: TasksSearch["age"], key: string): TasksSearch["age"] {
  if (current === key) return undefined;
  return key === "day" || key === "week" || key === "older" ? key : undefined;
}

export function Sidebar({
  data,
  search,
  set,
}: {
  data: TasksResponse | undefined;
  search: TasksSearch;
  set: (patch: Partial<TasksSearch>) => void;
}) {
  const view = search.view ?? "open";
  const types = new Set((search.type ?? "").split(",").filter(Boolean));
  const toggleType = (key: string) => {
    const next = new Set(types);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    set({ type: next.size === 0 ? undefined : [...next].join(",") });
  };
  return (
    <>
      <FilterGroup title="Status">
        <FilterOption
          type="radio"
          name="view"
          label="Open"
          checked={view === "open"}
          count={search.scope === "all" ? data?.counts.open : data?.counts.actionable}
          onChange={() => {
            set({ view: undefined });
          }}
        />
        <FilterOption
          type="radio"
          name="view"
          label="Done (last 30 days)"
          checked={view === "done"}
          count={data?.counts.done}
          onChange={() => {
            set({ view: "done", age: undefined });
          }}
        />
      </FilterGroup>
      {view === "open" ? (
        <FilterGroup title="Show">
          <FilterOption
            type="radio"
            name="scope"
            label="Can act on"
            checked={search.scope !== "all"}
            count={data?.counts.actionable}
            onChange={() => {
              set({ scope: undefined });
            }}
          />
          <FilterOption
            type="radio"
            name="scope"
            label="All tasks"
            checked={search.scope === "all"}
            count={data?.counts.open}
            onChange={() => {
              set({ scope: "all" });
            }}
          />
          {data ? <p className="m-0 mt-1 px-2 text-md text-muted">{data.scope_note}</p> : null}
        </FilterGroup>
      ) : null}
      <FilterGroup title="Type">
        {data?.facets.types.map((t) => (
          <FilterOption
            key={t.key}
            type="checkbox"
            name="type"
            label={t.label}
            checked={types.has(t.key)}
            count={t.count}
            onChange={() => {
              toggleType(t.key);
            }}
          />
        ))}
      </FilterGroup>
      {data && data.facets.people.length > 0 ? (
        <FilterGroup title="Person">
          {data.facets.people.map((p) => (
            <FilterOption
              key={p.key}
              type="checkbox"
              name="person"
              label={p.label}
              checked={search.person === p.key}
              count={p.count}
              onChange={() => {
                set({ person: search.person === p.key ? undefined : p.key });
              }}
            />
          ))}
        </FilterGroup>
      ) : null}
      {data && data.facets.platforms.length > 0 ? (
        <FilterGroup title="Platform">
          {data.facets.platforms.map((p) => (
            <FilterOption
              key={p.key}
              type="checkbox"
              name="platform"
              label={p.label}
              checked={search.platform === p.key}
              count={p.count}
              onChange={() => {
                set({ platform: search.platform === p.key ? undefined : p.key });
              }}
            />
          ))}
        </FilterGroup>
      ) : null}
      {view === "open" && data ? (
        <FilterGroup title="Age">
          {data.facets.ages.map((a) => (
            <FilterOption
              key={a.key}
              type="checkbox"
              name="age"
              label={a.label}
              checked={search.age === a.key}
              count={a.count}
              onChange={() => {
                set({ age: toggledAge(search.age, a.key) });
              }}
            />
          ))}
        </FilterGroup>
      ) : null}
    </>
  );
}
